import { useTranslation } from "next-i18next";
import { Controller, SubmitHandler, useFormContext } from "react-hook-form";
import {
  SubStepButtons,
  Form,
  Listbox,
  PostalCodeInputWithValidation,
} from "@talladega/components";
import GoogleMapsWrapper from "@modules/location/helpers/GoogleMapsWrapper";
import { Address } from "@common/types/Address";
import { provinces, Province } from "@talladega/types";
import { TranslationKeys } from "src/@types/i18next";
import AddressAutocompleteInput from "@common/components/form/AddressAutocompleteInput";
import {
  financialInfoMode,
  FinancingPreQualificationMode,
  personalInfoMode,
} from "../types/FinancingPreQualificationMode";
import { PreQualificationFormProps } from "../types/FinancingPreQualificationFormProps";

type Props = {
  onChangeMode(newMode: FinancingPreQualificationMode): void;
};

const getSelectedProvince = (province: string) => {
  return provinces.find((p) => p.abbr === province);
};

const FinancingPreQualificationResidenceInfo = ({ onChangeMode }: Props) => {
  const { t } = useTranslation(["common"]);

  const {
    register,
    control,
    setValue,
    handleSubmit,
    formState: { errors },
    reset,
    trigger,
    watch,
  } = useFormContext<PreQualificationFormProps>();

  const onFormSubmit: SubmitHandler<PreQualificationFormProps> = () => {
    onChangeMode(financialInfoMode);
  };

  const onBack: () => void = () => {
    onChangeMode(personalInfoMode);
  };

  const onAutoCompleteChange = (addressInput: Address | string) => {
    if (typeof addressInput === "string") {
      setValue("address", addressInput, { shouldValidate: true });

      // Attempt basic parsing of street number and name to handle typed address instead of autocompleted address
      const trimmed = addressInput.trim();
      const match = trimmed.match(/^(\d+)\s+(.*)$/);

      const streetNumber = match?.[1] ?? null;
      const streetName = match?.[2] ?? null;

      setValue("streetNumber", streetNumber);
      setValue("streetName", streetName);
      setValue("suiteNumber", "");

      trigger([
        "address",
        "streetNumber",
        "streetName",
        "city",
        "province",
        "postalCode",
      ]);
    } else {
      setValue("address", addressInput.address1, { shouldValidate: true });
      setValue("suiteNumber", addressInput.suiteNumber || null);
      setValue("streetNumber", addressInput.streetNumber || null);
      setValue("streetName", addressInput.streetName || null);
      setValue("city", addressInput.city, { shouldValidate: true });
      setValue("province", addressInput.provinceAbbr, { shouldValidate: true });
      setValue("postalCode", addressInput.postalCode, { shouldValidate: true });
    }
  };

  const onClearAddressForm = () => {
    reset({
      suiteNumber: "",
      streetNumber: "",
      streetName: "",
      city: "",
      province: "",
      postalCode: "",
    });
  };

  const selectedProvince = getSelectedProvince(watch("province"));

  return (
    <div className="w-full mb-5">
      <div>
        <form onSubmit={handleSubmit(onFormSubmit)}>
          <div className="flex flex-col gap-4">
            <GoogleMapsWrapper>
              <AddressAutocompleteInput
                label={t("common:street_address")}
                placeHolder={t("common:search_address")}
                onChange={(address) => {
                  onAutoCompleteChange(address);
                }}
                onClear={onClearAddressForm}
                showShortAddress
                onBlur={() => trigger("address")}
                feedback={errors.address?.message}
                status={errors.address ? "error" : "default"}
              />
            </GoogleMapsWrapper>
            <Form.Input
              type="text"
              className="w-full"
              label={t("common:city")}
              autoComplete="city"
              suppressHotjar
              feedback={errors.city?.message}
              status={errors.city ? "error" : "default"}
              maxLength={50}
              {...register("city", {
                required: {
                  value: true,
                  message: t("common:required"),
                },
                maxLength: 50,
                onBlur: () => trigger("city"),
              })}
            />
            <Controller
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t("common:required"),
                },
              }}
              name="province"
              render={({ field: { onChange, value, ref } }) => {
                const provValue = provinces.find((p) => p.abbr === value);

                return (
                  <Listbox
                    items={provinces}
                    label={t("common:province")}
                    keyOption={(item) => item.abbr}
                    displayOption={(item) =>
                      item?.name != null
                        ? t(item.name as TranslationKeys["common"])
                        : ""
                    }
                    displayButton={(item) =>
                      item?.name != null
                        ? t(item.name as TranslationKeys["common"])
                        : t("common:select_province")
                    }
                    valueOption={(item) => item}
                    status={errors.province ? "error" : "default"}
                    feedback={errors.province?.message}
                    onChange={(provinceInput: Province) => {
                      onChange(provinceInput.abbr);
                    }}
                    selectedItem={provValue}
                    defaultButtonClassName="text-slate-400"
                    menuPosition="Up"
                    buttonRef={ref}
                  />
                );
              }}
            />
            <PostalCodeInputWithValidation
              id="postalCode"
              className="w-full"
              feedback={errors?.postalCode?.message}
              status={errors?.postalCode ? "error" : "default"}
              register={register}
              fieldKey="postalCode"
              provinceToValidate={selectedProvince}
              required
            />
          </div>
          <div className="mt-6">
            <SubStepButtons
              backButton={{ onClick: onBack }}
              alignment="justify-between"
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default FinancingPreQualificationResidenceInfo;
